/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { createSelector } from '@reduxjs/toolkit';
import { selectorMemoizationOptions } from '@opengeoweb/store';
import type { FilterState } from '../../components/FilterList/filter-hooks';
import { initialFilters } from '../../components/FilterList/filter-utils';
import type { WarningModuleStore } from '../types';
import { defaultExpandedWarningSections, initialState } from './reducer';
import type {
  ExpandedWarningSections,
  WarningsFilterPanelState,
  WarningsFiltersState,
} from './types';

export const getWarningsFilters = (
  store: WarningModuleStore,
): WarningsFiltersState => {
  if (store?.warningsFilters) {
    return store.warningsFilters;
  }
  return initialState;
};

const getWarningsFilterPanelById = createSelector(
  getWarningsFilters,
  (_store: WarningModuleStore, panelId?: string) => panelId,
  (filtersState, panelId): WarningsFilterPanelState | undefined =>
    panelId ? filtersState?.[panelId] : undefined,
  selectorMemoizationOptions,
);

const getFilterStateFromPanelState = (
  panelState?: WarningsFilterPanelState,
): FilterState => {
  if (!panelState) {
    return initialFilters;
  }

  const { expandedSections, hoveredWarningIds, ...filterState } = panelState;
  void expandedSections;
  void hoveredWarningIds;
  return filterState;
};

export const getWarningsFilterById = createSelector(
  getWarningsFilterPanelById,
  (panelState): FilterState => getFilterStateFromPanelState(panelState),
  selectorMemoizationOptions,
);

export const getExpandedSectionsByPanelId = createSelector(
  getWarningsFilterPanelById,
  (panelState): ExpandedWarningSections =>
    panelState?.expandedSections ?? defaultExpandedWarningSections,
  selectorMemoizationOptions,
);

export const getHoveredWarningIdsByPanelId = createSelector(
  getWarningsFilterPanelById,
  (panelState): string[] => panelState?.hoveredWarningIds || [],
  selectorMemoizationOptions,
);

export const getWarningsPresetById = createSelector(
  getWarningsFilterPanelById,
  (panelState) => {
    if (!panelState) {
      return { filterState: null, expandedSections: null };
    }
    return {
      filterState: getFilterStateFromPanelState(panelState),
      expandedSections: panelState.expandedSections,
    };
  },
  selectorMemoizationOptions,
);
